/* ==========================================================================
   Future Aesthetics — product card behaviour
   <fa-quick-add>  adds a variant straight to the basket from a card
   swatch clicks   swap the card image and deep-link to the chosen variant
   ========================================================================== */

(function () {
  'use strict';

  const ADDED_LABEL_MS = 1400;
  const RELEASE_DISTANCE = 60;

  function getCartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  class FaQuickAdd extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.feedback = this.querySelector('.fa-quick-add__feedback');
      this.addEventListener('click', this.onClick.bind(this));
    }

    onClick(event) {
      const button = event.target.closest('[data-variant-id]');
      if (!button || button.disabled) return;

      event.preventDefault();
      this.pointer = { x: event.clientX, y: event.clientY };
      this.add(button, button.dataset.variantId);
    }

    add(button, variantId) {
      if (button.classList.contains('is-loading')) return;

      this.setFeedback('');
      button.classList.add('is-loading');

      const cart = getCartElement();
      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', 1);

      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.append('sections', cart.getSectionsToRender().map((section) => section.id));
        body.append('sections_url', window.location.pathname);
        cart.setActiveElement(document.activeElement);
      }

      fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.setFeedback(response.description || response.message || 'Unavailable');
            return;
          }

          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }

          button.classList.add('is-added');
          setTimeout(() => button.classList.remove('is-added'), ADDED_LABEL_MS);
          this.dismiss(button);

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'fa-quick-add',
              productVariantId: variantId,
              cartData: response,
            });
          }

          cart.classList.remove('is-empty');
          cart.renderContents(response);
        })
        .catch(() => this.setFeedback('Could not add. Try again.'))
        .finally(() => button.classList.remove('is-loading'));
    }

    /**
     * Drop the size panel once the item is in the basket.
     *
     * The panel is held open by the pointer still resting on the card, so after
     * adding it stayed on screen with the chosen size still lit — and came
     * straight back the moment the cart drawer was closed. Touch layouts render
     * the panel inline as part of the card, so they are left alone.
     */
    dismiss(button) {
      const card = this.closest('[data-fa-card]');
      if (!card || !window.matchMedia('(hover: hover)').matches) return;

      if (button) button.blur();
      card.classList.add('is-quick-add-dismissed');

      // Releasing on mouseleave is no good: the drawer opening over the card
      // fires one immediately. Wait for the pointer to actually travel, which
      // is the point at which the shopper has moved on.
      const from = this.pointer || { x: 0, y: 0 };
      const release = (event) => {
        if (Math.hypot(event.clientX - from.x, event.clientY - from.y) < RELEASE_DISTANCE) return;
        document.removeEventListener('mousemove', release);
        card.classList.remove('is-quick-add-dismissed');
      };
      document.addEventListener('mousemove', release);
    }

    setFeedback(message) {
      if (this.feedback) this.feedback.textContent = message;
    }
  }

  if (!customElements.get('fa-quick-add')) {
    customElements.define('fa-quick-add', FaQuickAdd);
  }

  /* ------------------------------------------------------------------------
     Colour swatches: preview the colour on the card without leaving the page.
     ------------------------------------------------------------------------ */

  document.addEventListener('click', (event) => {
    const swatch = event.target.closest('.fa-card__swatch');
    if (!swatch) return;

    event.preventDefault();

    const card = swatch.closest('[data-fa-card]');
    if (!card) return;

    card
      .querySelectorAll('.fa-card__swatch')
      .forEach((item) => item.classList.toggle('is-active', item === swatch));

    showColourImage(card, swatch.dataset.swatchImage);

    if (swatch.dataset.swatchUrl) {
      card.querySelectorAll('a[href]').forEach((link) => {
        link.href = swatch.dataset.swatchUrl;
      });
    }

    repointQuickAdd(card, swatch.dataset.swatchName);
  });

  /**
   * Show the chosen colour on the card.
   *
   * Both the primary and the hover image are repointed. The hover image is a
   * fixed second product shot, so leaving it alone meant that while the pointer
   * was over the card — which it always is when a swatch is clicked — the
   * visible image was the old one, and the colour only appeared once the
   * pointer left. Pointing both at the chosen colour keeps the card on that
   * colour whether or not it is hovered.
   *
   * The swap waits on a preload so the card never flashes empty mid-download.
   */
  function showColourImage(card, url) {
    if (!url) return;

    const images = card.querySelectorAll('.fa-card__image');
    if (!images.length) return;

    const apply = () => {
      images.forEach((image) => {
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = url;
      });
    };

    const preload = new Image();
    preload.src = url;
    if (preload.complete) {
      apply();
    } else {
      preload.addEventListener('load', apply, { once: true });
      preload.addEventListener('error', apply, { once: true });
    }
  }

  /**
   * Re-point the quick-add size buttons at the chosen colour. Without this the
   * buttons keep the variant ids the card was rendered with, so picking a
   * swatch and then a size added the original colour to the cart.
   */
  function repointQuickAdd(card, colour) {
    if (!colour) return;

    const json = card.querySelector('[data-fa-variants]');
    const sizeButtons = card.querySelectorAll('.fa-quick-add__option[data-size]');
    if (!json || !sizeButtons.length) return;

    let variants;
    try {
      variants = JSON.parse(json.textContent);
    } catch (error) {
      return;
    }

    sizeButtons.forEach((button) => {
      const match = variants.find(
        (v) => v.colour === colour && v.size === button.dataset.size
      );

      if (!match) {
        button.disabled = true;
        button.classList.add('is-unavailable');
        return;
      }

      button.dataset.variantId = match.id;
      button.disabled = !match.available;
      button.classList.toggle('is-unavailable', !match.available);
    });
  }
})();
